import { baseApi } from './baseApi';

export interface TaskOccurrence {
  id: number;
  status: string;
  timestamp?: string | null;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
  taskId?: number;
  siteId?: number;
  name?: string | null;
  type?: string | null;
  priority?: string | null;
  capacity?: number | null;
  scheduleCarCount?: number | null;
  carCount?: number | null;
  notes?: string | null;
  employeeId?: number | null;
  completedAt?: string | null;
  completedById?: number | null;
  // Seeded from TaskTemplate.videos on first insert; editable per-occurrence.
  videos?: string[] | null;
  createdAt?: string;
  archivedAt?: string | null;
  task?: {
    id: number;
    name?: string | null;
    subTitle?: string | null;
    interval?: string | null;
    type?: string | null;
    priority?: string | null;
  } | null;
}

export interface ListTaskOccurrencesParams {
  status?: string;
  type?: string;
  interval?: string;
  employeeId?: number;
  fromDate?: string;
  untilDate?: string;
  page?: number;
  pageSize?: number;
  key?: string;
  direction?: 'asc' | 'desc';
}

export interface ListTaskOccurrencesResponse {
  results: TaskOccurrence[];
  total: number;
  page: number;
  pageSize: number;
}

export const TASK_TEMPLATE_DEPENDENCIES = [
  'DoneBefore',
  'DoneAfter',
  'BlockedBy',
  'RelatesTo',
  'SubTask',
] as const;
export type TaskTemplateDependency = (typeof TASK_TEMPLATE_DEPENDENCIES)[number];

export interface TaskTemplateLink {
  id: number;
  taskId: number;
  linkedTaskId: number;
  dependency: TaskTemplateDependency;
  name?: string | null;
  status?: string | null;
  archivedAt?: string | null;
  createdAt?: string;
}

export interface LinkedTaskTemplateInput {
  id: number;
  dependency: TaskTemplateDependency;
}

export interface TaskTemplate {
  id: number;
  name: string;
  subTitle?: string | null;
  description?: string | null;
  start: string;
  end?: string | null;
  type: string;
  interval: string;
  intervalCount?: number;
  weeklySchedule?: string | null;
  hourlySchedule?: number[] | null;
  monthlySameWeekday?: boolean | null;
  alertIfOverdue?: boolean | null;
  startingCarCount?: number | null;
  intervalCarCount?: number | null;
  skipCompletionsNumber?: number | null;
  priority?: string;
  capacity?: number | null;
  estimatedTime?: string | null;
  employeeId?: number | null;
  locationId?: number | null;
  operationTypeId?: number | null;
  prefixId?: number | null;
  resource?: string | null;
  videos?: string[] | null;
  siteId?: number | null;
  // groupId (with siteId IS NULL) marks master templates; the scheduler fans them out.
  groupId?: number | null;
  archivedAt?: string | null;
  createdAt?: string;
  latestOccurrence?: TaskOccurrence | null;
  linkedTaskTemplates?: TaskTemplateLink[];
}

export interface ListTaskTemplatesParams {
  type?: string;
  interval?: string;
  page?: number;
  pageSize?: number;
  key?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  status?: string | string[];
  employeeId?: number | number[];
  prefix?: string | string[];
  dueDateFrom?: string;
  dueDateUntil?: string;
  from?: string;
  until?: string;
}

export interface ListTaskTemplatesResponse {
  results: TaskTemplate[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateTaskTemplateRequest {
  name: string;
  type: string;
  interval: string;
  start: string;
  subTitle?: string | null;
  description?: string | null;
  end?: string | null;
  intervalCount?: number;
  weeklySchedule?: string | null;
  hourlySchedule?: number[] | null;
  monthlySameWeekday?: boolean | null;
  alertIfOverdue?: boolean | null;
  startingCarCount?: number | null;
  intervalCarCount?: number | null;
  skipCompletionsNumber?: number | null;
  priority?: string;
  capacity?: number | null;
  estimatedTime?: string | null;
  employeeId?: number | null;
  locationId?: number | null;
  operationTypeId?: number | null;
  prefixId?: number | null;
  resource?: string | null;
  videos?: string[] | null;
  linkedTaskTemplates?: LinkedTaskTemplateInput[];
}

export type UpdateTaskTemplateRequest = Partial<CreateTaskTemplateRequest> & { id: number };

export interface UnlinkTaskTemplateRequest {
  taskId: number;
  linkId: number;
}

export const TASK_TEMPLATE_STATUSES = [
  'Pending',
  'Planning',
  'InProgress',
  'Review',
  'Completed',
  'OnHold',
  'Snoozed',
  'Expired',
] as const;
export type TaskTemplateStatus = (typeof TASK_TEMPLATE_STATUSES)[number];

export interface SetOccurrenceStatusRequest {
  taskId: number;
  status: TaskTemplateStatus;
  notes?: string;
}

export interface RevertOccurrenceRequest {
  occurrenceId: number;
}

export interface UpdateOccurrenceRequest {
  occurrenceId: number;
  notes?: string | null;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
  employeeId?: number | null;
  priority?: string | null;
  capacity?: number | null;
  name?: string | null;
  type?: string | null;
  scheduleCarCount?: number | null;
  carCount?: number | null;
  completedAt?: string | null;
  completedById?: number | null;
  videos?: string[] | null;
}

// Audit row for an occurrence status change; `employee` is eager-joined server-side.
export interface OccurrenceHistoryEntry {
  id: number;
  occurrenceId: number;
  taskId: number;
  siteId: number;
  fromStatus: string | null;
  toStatus: string;
  employeeId: number | null;
  reason: string | null;
  createdAt: string;
  creator: string;
  employee?: { id: number; firstName?: string | null; lastName?: string | null } | null;
}

// Flattened (note ⨯ join) row; createdAt/creator reflect the "added" event.
export interface OccurrenceNoteEntry {
  occurrenceId: number;
  noteId: number;
  text: string | null;
  siteId: number | null;
  createdAt: string;
  creator: string;
}

// FileMetadata for an occurrence; `downloadUrl` is gateway-signed on the way out.
export interface OccurrenceFileEntry {
  id: number;
  key: string;
  size: number;
  originalName: string;
  mimeType: string;
  // Free-form caption captured by UploadSection's UPSERT_FILE dialog.
  description?: string | null;
  downloadUrl?: string | null;
  archivedAt?: string | null;
  createdAt?: string;
  creator?: string;
}

const TASK_TEMPLATE_LIST_TAG = { type: 'TaskTemplate' as const, id: 'LIST' };
const TASK_OCCURRENCE_LIST_TAG = { type: 'TaskTemplate' as const, id: 'OCCURRENCE_LIST' };
const occurrenceHistoryTag = (occurrenceId: number) => ({
  type: 'TaskTemplate' as const,
  id: `occurrence-history-${occurrenceId}`,
});
const occurrenceNotesTag = (occurrenceId: number) => ({
  type: 'TaskTemplate' as const,
  id: `occurrence-notes-${occurrenceId}`,
});
const occurrenceFilesTag = (occurrenceId: number) => ({
  type: 'TaskTemplate' as const,
  id: `occurrence-files-${occurrenceId}`,
});
// Distinct from `occurrenceFilesTag` so Media and Attachments invalidate independently.
const occurrenceMediaTag = (occurrenceId: number) => ({
  type: 'TaskTemplate' as const,
  id: `occurrence-media-${occurrenceId}`,
});
const taskTemplateMediaTag = (taskTemplateId: number) => ({
  type: 'TaskTemplate' as const,
  id: `task-template-media-${taskTemplateId}`,
});

export const taskTemplatesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTaskTemplates: builder.query<ListTaskTemplatesResponse, ListTaskTemplatesParams | void>({
      query: (params) => ({
        url: 'task-templates',
        params: params || {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'TaskTemplate' as const, id })),
              TASK_TEMPLATE_LIST_TAG,
            ]
          : [TASK_TEMPLATE_LIST_TAG],
    }),
    getTaskTemplate: builder.query<TaskTemplate, number>({
      query: (id) => ({ url: `task-templates/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'TaskTemplate', id }],
    }),
    createTaskTemplate: builder.mutation<TaskTemplate, CreateTaskTemplateRequest>({
      query: (body) => ({
        url: 'task-templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TASK_TEMPLATE_LIST_TAG],
    }),
    updateTaskTemplate: builder.mutation<TaskTemplate, UpdateTaskTemplateRequest>({
      query: ({ id, ...patch }) => ({
        url: `task-templates/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TaskTemplate', id },
        TASK_TEMPLATE_LIST_TAG,
      ],
    }),
    deleteTaskTemplate: builder.mutation<{ id: number; archived: boolean }, number>({
      query: (id) => ({
        url: `task-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'TaskTemplate', id },
        TASK_TEMPLATE_LIST_TAG,
      ],
    }),
    unlinkTaskTemplate: builder.mutation<TaskTemplateLink, UnlinkTaskTemplateRequest>({
      query: ({ linkId }) => ({
        url: `task-templates/${linkId}/unlink`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskTemplate' as const, id: taskId },
        TASK_TEMPLATE_LIST_TAG,
      ],
    }),
    getTaskOccurrences: builder.query<
      ListTaskOccurrencesResponse,
      ListTaskOccurrencesParams | void
    >({
      query: (params) => ({
        url: 'task-occurrences',
        params: params || {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: 'TaskTemplate' as const,
                id: `occurrence-${id}`,
              })),
              TASK_OCCURRENCE_LIST_TAG,
            ]
          : [TASK_OCCURRENCE_LIST_TAG],
    }),
    runOccurrenceScheduler: builder.mutation<unknown, void>({
      query: () => ({
        url: 'manager/execute-task/create-task-occurrences',
        method: 'POST',
        body: { synchronous: true },
      }),
      invalidatesTags: [TASK_TEMPLATE_LIST_TAG, TASK_OCCURRENCE_LIST_TAG],
    }),
    setOccurrenceStatus: builder.mutation<unknown, SetOccurrenceStatusRequest>({
      query: ({ taskId, status, notes }) => ({
        url: 'task-occurrences/status',
        method: 'POST',
        body: { taskIds: [taskId], status, notes },
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskTemplate', id: taskId },
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
    setOccurrenceStatusById: builder.mutation<
      unknown,
      {
        occurrenceId: number;
        status: TaskTemplateStatus;
        notes?: string;
        employeeId?: number | null;
      }
    >({
      query: ({ occurrenceId, status, notes, employeeId }) => ({
        url: `task-occurrences/${occurrenceId}/status`,
        method: 'POST',
        body: { status, notes, employeeId },
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: 'TaskTemplate' as const, id: `occurrence-${occurrenceId}` },
        occurrenceHistoryTag(occurrenceId),
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
    updateOccurrence: builder.mutation<TaskOccurrence, UpdateOccurrenceRequest>({
      query: ({ occurrenceId, ...patch }) => ({
        url: `task-occurrences/${occurrenceId}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: 'TaskTemplate' as const, id: `occurrence-${occurrenceId}` },
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
    getOccurrenceHistory: builder.query<OccurrenceHistoryEntry[], number>({
      query: (occurrenceId) => ({ url: `task-occurrences/${occurrenceId}/history` }),
      providesTags: (_result, _error, occurrenceId) => [occurrenceHistoryTag(occurrenceId)],
    }),
    getOccurrenceNotes: builder.query<OccurrenceNoteEntry[], number>({
      query: (occurrenceId) => ({ url: `task-occurrences/${occurrenceId}/notes` }),
      providesTags: (_result, _error, occurrenceId) => [occurrenceNotesTag(occurrenceId)],
    }),
    addOccurrenceNote: builder.mutation<
      OccurrenceNoteEntry,
      { occurrenceId: number; text: string }
    >({
      query: ({ occurrenceId, text }) => ({
        url: `task-occurrences/${occurrenceId}/notes`,
        method: 'POST',
        body: { text },
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [occurrenceNotesTag(occurrenceId)],
    }),
    deleteOccurrenceNote: builder.mutation<
      { occurrenceId: number; noteId: number; archived: boolean },
      { occurrenceId: number; noteId: number }
    >({
      query: ({ occurrenceId, noteId }) => ({
        url: `task-occurrences/${occurrenceId}/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [occurrenceNotesTag(occurrenceId)],
    }),
    // Multipart upload lives in core/api/files.js; consumer invalidates occurrenceFilesTag after.
    getOccurrenceFiles: builder.query<OccurrenceFileEntry[], number>({
      query: (occurrenceId) => ({ url: `task-occurrences/${occurrenceId}/files` }),
      providesTags: (_result, _error, occurrenceId) => [occurrenceFilesTag(occurrenceId)],
    }),
    archiveOccurrenceFile: builder.mutation<
      OccurrenceFileEntry,
      { occurrenceId: number; fileId: number }
    >({
      query: ({ occurrenceId, fileId }) => ({
        url: `task-occurrences/${occurrenceId}/files/${fileId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [occurrenceFilesTag(occurrenceId)],
    }),
    unarchiveOccurrenceFile: builder.mutation<
      OccurrenceFileEntry,
      { occurrenceId: number; fileId: number }
    >({
      query: ({ occurrenceId, fileId }) => ({
        url: `task-occurrences/${occurrenceId}/files/${fileId}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [occurrenceFilesTag(occurrenceId)],
    }),
    // Same shape as files, separate join table; multipart upload in files.js.
    getOccurrenceMedia: builder.query<OccurrenceFileEntry[], number>({
      query: (occurrenceId) => ({ url: `task-occurrences/${occurrenceId}/media` }),
      providesTags: (_result, _error, occurrenceId) => [occurrenceMediaTag(occurrenceId)],
    }),
    archiveOccurrenceMedia: builder.mutation<
      OccurrenceFileEntry,
      { occurrenceId: number; fileId: number }
    >({
      query: ({ occurrenceId, fileId }) => ({
        url: `task-occurrences/${occurrenceId}/media/${fileId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [occurrenceMediaTag(occurrenceId)],
    }),
    unarchiveOccurrenceMedia: builder.mutation<
      OccurrenceFileEntry,
      { occurrenceId: number; fileId: number }
    >({
      query: ({ occurrenceId, fileId }) => ({
        url: `task-occurrences/${occurrenceId}/media/${fileId}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [occurrenceMediaTag(occurrenceId)],
    }),
    // Site-scoped TaskTemplate media; group variants below share the same tag.
    getTaskTemplateMedia: builder.query<OccurrenceFileEntry[], number>({
      query: (taskTemplateId) => ({ url: `task-templates/${taskTemplateId}/media` }),
      providesTags: (_result, _error, taskTemplateId) => [taskTemplateMediaTag(taskTemplateId)],
    }),
    archiveTaskTemplateMedia: builder.mutation<
      OccurrenceFileEntry,
      { taskTemplateId: number; fileId: number }
    >({
      query: ({ taskTemplateId, fileId }) => ({
        url: `task-templates/${taskTemplateId}/media/${fileId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { taskTemplateId }) => [
        taskTemplateMediaTag(taskTemplateId),
      ],
    }),
    unarchiveTaskTemplateMedia: builder.mutation<
      OccurrenceFileEntry,
      { taskTemplateId: number; fileId: number }
    >({
      query: ({ taskTemplateId, fileId }) => ({
        url: `task-templates/${taskTemplateId}/media/${fileId}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { taskTemplateId }) => [
        taskTemplateMediaTag(taskTemplateId),
      ],
    }),
    // Group variants: same join table, prefixed with `manager/` to bypass baseApi's site prefix.
    getTaskTemplateMediaForGroup: builder.query<
      OccurrenceFileEntry[],
      { groupId: number; taskTemplateId: number }
    >({
      query: ({ groupId, taskTemplateId }) => ({
        url: `manager/groups/${groupId}/task-templates/${taskTemplateId}/media`,
      }),
      providesTags: (_result, _error, { taskTemplateId }) => [taskTemplateMediaTag(taskTemplateId)],
    }),
    archiveTaskTemplateMediaForGroup: builder.mutation<
      OccurrenceFileEntry,
      { groupId: number; taskTemplateId: number; fileId: number }
    >({
      query: ({ groupId, taskTemplateId, fileId }) => ({
        url: `manager/groups/${groupId}/task-templates/${taskTemplateId}/media/${fileId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { taskTemplateId }) => [
        taskTemplateMediaTag(taskTemplateId),
      ],
    }),
    unarchiveTaskTemplateMediaForGroup: builder.mutation<
      OccurrenceFileEntry,
      { groupId: number; taskTemplateId: number; fileId: number }
    >({
      query: ({ groupId, taskTemplateId, fileId }) => ({
        url: `manager/groups/${groupId}/task-templates/${taskTemplateId}/media/${fileId}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { taskTemplateId }) => [
        taskTemplateMediaTag(taskTemplateId),
      ],
    }),
    // Group-scoped TaskTemplates; `manager/` prefix bypasses the site-scoped baseApi URL builder.
    getGroupTaskTemplates: builder.query<
      ListTaskTemplatesResponse,
      { groupId: number; params?: ListTaskTemplatesParams }
    >({
      query: ({ groupId, params }) => ({
        url: `manager/groups/${groupId}/task-templates`,
        params: params || {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'TaskTemplate' as const, id })),
              { type: 'TaskTemplate' as const, id: 'GROUP_LIST' },
            ]
          : [{ type: 'TaskTemplate' as const, id: 'GROUP_LIST' }],
    }),
    createGroupTaskTemplate: builder.mutation<
      TaskTemplate,
      { groupId: number; body: CreateTaskTemplateRequest }
    >({
      query: ({ groupId, body }) => ({
        url: `manager/groups/${groupId}/task-templates`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'TaskTemplate' as const, id: 'GROUP_LIST' }],
    }),
    updateGroupTaskTemplate: builder.mutation<
      TaskTemplate,
      { groupId: number; id: number; patch: Partial<CreateTaskTemplateRequest> }
    >({
      query: ({ groupId, id, patch }) => ({
        url: `manager/groups/${groupId}/task-templates/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TaskTemplate' as const, id },
        { type: 'TaskTemplate' as const, id: 'GROUP_LIST' },
      ],
    }),
    deleteGroupTaskTemplate: builder.mutation<
      { id: number; archived: boolean },
      { groupId: number; id: number }
    >({
      query: ({ groupId, id }) => ({
        url: `manager/groups/${groupId}/task-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TaskTemplate' as const, id },
        { type: 'TaskTemplate' as const, id: 'GROUP_LIST' },
      ],
    }),
    completeOccurrence: builder.mutation<unknown, { taskId: number; notes?: string }>({
      query: ({ taskId, notes }) => ({
        url: 'task-occurrences/complete',
        method: 'POST',
        body: { taskIds: [taskId], notes },
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskTemplate', id: taskId },
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
    snoozeOccurrence: builder.mutation<unknown, { taskId: number; notes?: string }>({
      query: ({ taskId, notes }) => ({
        url: 'task-occurrences/snooze',
        method: 'POST',
        body: { taskIds: [taskId], notes },
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskTemplate', id: taskId },
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
    revertOccurrenceCompletion: builder.mutation<unknown, RevertOccurrenceRequest>({
      query: ({ occurrenceId }) => ({
        url: 'task-occurrences/revert-completion',
        method: 'POST',
        body: { occurrenceIds: [occurrenceId] },
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: 'TaskTemplate' as const, id: `occurrence-${occurrenceId}` },
        occurrenceHistoryTag(occurrenceId),
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
    revertOccurrenceSnooze: builder.mutation<unknown, RevertOccurrenceRequest>({
      query: ({ occurrenceId }) => ({
        url: 'task-occurrences/revert-snoozed',
        method: 'POST',
        body: { occurrenceIds: [occurrenceId] },
      }),
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: 'TaskTemplate' as const, id: `occurrence-${occurrenceId}` },
        occurrenceHistoryTag(occurrenceId),
        TASK_TEMPLATE_LIST_TAG,
        TASK_OCCURRENCE_LIST_TAG,
      ],
    }),
  }),
});

export const {
  useGetTaskTemplatesQuery,
  useGetTaskTemplateQuery,
  useGetTaskOccurrencesQuery,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
  useDeleteTaskTemplateMutation,
  useUnlinkTaskTemplateMutation,
  useRunOccurrenceSchedulerMutation,
  useSetOccurrenceStatusMutation,
  useSetOccurrenceStatusByIdMutation,
  useUpdateOccurrenceMutation,
  useCompleteOccurrenceMutation,
  useSnoozeOccurrenceMutation,
  useRevertOccurrenceCompletionMutation,
  useRevertOccurrenceSnoozeMutation,
  useGetOccurrenceHistoryQuery,
  useGetOccurrenceNotesQuery,
  useAddOccurrenceNoteMutation,
  useDeleteOccurrenceNoteMutation,
  useGetOccurrenceFilesQuery,
  useArchiveOccurrenceFileMutation,
  useUnarchiveOccurrenceFileMutation,
  useGetOccurrenceMediaQuery,
  useArchiveOccurrenceMediaMutation,
  useUnarchiveOccurrenceMediaMutation,
  useGetTaskTemplateMediaQuery,
  useArchiveTaskTemplateMediaMutation,
  useUnarchiveTaskTemplateMediaMutation,
  useGetTaskTemplateMediaForGroupQuery,
  useArchiveTaskTemplateMediaForGroupMutation,
  useUnarchiveTaskTemplateMediaForGroupMutation,
  useGetGroupTaskTemplatesQuery,
  useCreateGroupTaskTemplateMutation,
  useUpdateGroupTaskTemplateMutation,
  useDeleteGroupTaskTemplateMutation,
} = taskTemplatesApi;
